// Public, verifiable facts the website states, in one place so they can be
// re-checked before every release (website build brief sections 2 and 8:
// verify security copy against the actual deployment before publishing).
//
// Checked 2026-10-12 (the staking upgrade: docs/evidence/mainnet/2026-10-10/upgrade-receipt.json; the
// reviews to R95: docs/reviews/). First checked 2026-09-29 against the chain (getAccountInfo of the program and
// its ProgramData on mainnet), docs/evidence/mainnet/2026-09-24/
// deploy-receipt.json, docs/evidence/mainnet/2026-09-28/upgrade-receipt.json,
// docs/01-architecture.md, docs/reviews/ and the public technical brief.
// When the deployment, the authority or the review state changes, update
// this file and the date together.

import { PUBLISHED_ARTIFACT_LEN, PUBLISHED_SHA256 } from '../program';

export const FACTS_CHECKED = '12 October 2026';

export const NETWORK = 'Solana mainnet';
export const PROGRAM_ID = 'BsKbo3shF4X5nG3sMcBApDGcDM7ocC7Gun4ycBqsfrRo';
export const PROGRAM_DATA = 'DxHi9JBoD3gU5pZej3yYB2goJLh7nuaspZ5JJcDhorgz';
export const FIRST_DEPLOYED = '24 September 2026';
export const LAST_UPGRADED = '10 October 2026';
export const LAST_UPGRADE_SLOT = '455,374,559';
export const ARTIFACT_BYTES = PUBLISHED_ARTIFACT_LEN.toLocaleString('en-US');
export const ARTIFACT_SHA256 = PUBLISHED_SHA256;
export const UPGRADE_AUTHORITY = '7F8kuQEyvsfAwCn4UyoZ7grgGSwKYMrABZHb41eYVDR6';
export const TREASURY_VAULT = '3VXKr5pcfq8D7nfphRHAt6z8oS7RZHCcNRLrqyxTzo1D';

/** The swap fee, as the program enforces it (program/src/fee.rs FEE_BPS). */
export const FEE_PERCENT = '0.30%';

/**
 * One-time keys (XMSS leaves) in each key tree: docs/01-architecture.md
 * section 3, "tree height 8 (256 leaves)".
 */
export const ONE_TIME_KEYS_PER_TREE = 256;

/**
 * The entrance's advisory slot (app/art/welcome/PLAN.md section 4.0): one
 * line under the header on /welcome, /locked, /create and /restore, shown
 * only while this is set, so an incident notice is never improvised.
 * Unset on 2026-10-03: there is no advisory.
 */
export const ADVISORY: string | null = null;

/**
 * The site's canonical domain, for the Restore notice's "type them only
 * here" (PLAN.md section 5). Null on 2026-10-03: the custom domain is not
 * settled yet (docs/10-hosting.md), and the notice says "here" until it is.
 */
export const SITE_DOMAIN: string | null = null;

export function explorerAddress(address: string): string {
  return `https://explorer.solana.com/address/${address}`;
}

/** First 8 and last 4 hex digits; word joiners keep it from breaking at the ellipsis. */
export function shortHash(hex: string): string {
  return `${hex.slice(0, 8)}\u2060…\u2060${hex.slice(-4)}`;
}


/** Gate reviews to date (docs/reviews/). Every one was run by an AI reviewer, not a human auditor. */
export const REVIEWS: Array<{ gate: string; date: string; scope: string }> = [
  { gate: 'Assessment', date: '15 Sep', scope: 'First assessment of the research prototype' },
  { gate: 'G1', date: '16 Sep', scope: 'Program design; the first recovery design was withdrawn' },
  { gate: 'G2', date: '17 Sep', scope: 'Real swap routes through the vault; the new recovery design' },
  { gate: 'G3', date: '18 Sep', scope: 'TypeScript SDK, signer, key ledger and recovery' },
  { gate: 'G4', date: '19 Sep', scope: 'Devnet lifecycle' },
  { gate: 'G5, part 1', date: '24 Sep', scope: 'The protocol fee' },
  { gate: 'G5', date: '26 Sep', scope: 'The app and the SDK, end to end' },
  { gate: 'G6', date: '27 Sep', scope: 'Which side of a swap pays the fee; the reviewer rebuilt the deployed program' },
  { gate: 'Staking', date: '5 Oct', scope: 'Native staking from inside the vault: the design, then its closure' },
  { gate: 'Upgrade', date: '6 Oct', scope: 'Transaction capacity and the mainnet upgrade procedure' },
  { gate: 'Recovery records', date: '6–8 Oct', scope: 'Recovering an older key tree safely after a restore' },
  { gate: 'Fee reserve', date: '7 Oct', scope: 'One fee reserve per vault, derived from the 24 words' },
  { gate: 'Fee reserve release', date: '8–10 Oct', scope: 'Built and tested in four reviewed stages, devnet included; closed 10 Oct' },
];
